import React, {useEffect, useRef} from 'react'
import {BaseButton, Box, Flex, Text, useDisclosure} from '../../../../vanilla'
import {useRapidDeliveryStore, useStoreDetailsStore} from '../../../../store/hooks/useStore'
import {observer} from 'mobx-react'
import {RapidDeliveryConfirmationModal} from '../rapid-delivery-confirmation-modal'
import {Countdown} from '../../../../components/book-delivery/reservation-status'
import {CheckIcon} from '../../../../components/icons'
import {isMobile} from '../../../../utils/utils'
import {useAddShippingDetailsTracking, useViewShippingDetailsTracking} from '../../../../analytics/shipping-tracking'

export const RapidDelivery = observer(() => {
  const {
    isRapidDeliveryBooked,
    rapidDeliveryDeliveryEstimate,
    bookRapidDelivery,
    rapidDeliveryShippingCost,
    rapidDeliveryReservation,
  } = useRapidDeliveryStore()
  const { currentDeliveryStore } = useStoreDetailsStore()
  const sendViewShippingDetailsData = useViewShippingDetailsTracking()
  const sendAddShippingDetailsData = useAddShippingDetailsTracking()
  const hasTrackedRapidViewRef = useRef(false)

  const confirmationModal = useDisclosure(false)

  const handleBookRapidDelivery = async () => {
    try {
      confirmationModal.onOpen()
      await bookRapidDelivery()
      const rapidSlotId = 'rapid-delivery'

      sendAddShippingDetailsData({
        deliveryDay: {
          isRapid: true,
        },
        slot: {
          id: rapidSlotId,
          available: true,
          storeId: currentDeliveryStore?.id ?? '',
          shippingCost: rapidDeliveryShippingCost ?? rapidDeliveryReservation?.shippingCost ?? 0,
          windowStartTime: rapidDeliveryReservation?.windowStartTime ?? '',
          startHour: '',
          endHour: '',
        },
        currentDeliverySlotId: rapidSlotId,
      })

    } catch (error) {
      console.error('Failed to book rapid delivery:', error)
    }
  }

  const deliveryEstimate = rapidDeliveryDeliveryEstimate ?? 60
  const expiryTime = rapidDeliveryReservation?.expiryTime

  // Fire view_shipping_details event
  useEffect(() => {
    if (hasTrackedRapidViewRef.current) {
      return
    }

    hasTrackedRapidViewRef.current = true

    const rapidSlotId = 'rapid-delivery'

    sendViewShippingDetailsData({
      deliveryDay: {
        isRapid: true,
      },
      slots: [
        {
          id: rapidSlotId,
          available: true,
          storeId: currentDeliveryStore?.id ?? '',
          shippingCost: rapidDeliveryShippingCost ?? rapidDeliveryShippingCost ?? 0,
          windowStartTime: rapidDeliveryReservation?.windowStartTime ?? new Date().toISOString(),
          startHour: '',
          endHour: '',
        },
      ],
      currentDeliverySlotId: isRapidDeliveryBooked ? rapidSlotId : '',
      slotsMatch: (slotId: string, selectedSlotId: string) => slotId === selectedSlotId,
    })
  }, [
    currentDeliveryStore?.id,
    isRapidDeliveryBooked,
    rapidDeliveryShippingCost,
    rapidDeliveryReservation?.windowStartTime
  ])

  const getButtonText = () => {
    let text = 'Book Rapid Now'
    if (isRapidDeliveryBooked) {
      text = 'Booked'
    }
    if (isMobile() && !isRapidDeliveryBooked) {
      text = 'Book Now'
    }
    return text
  }

  return (
    <>
      <Flex
        flexDirection="column"
        backgroundColor="white"
        width="full"
        paddingX={["16px", "24px"]}
        paddingTop="18px"
        paddingBottom="24px"
        alignItems="flex-start"
        data-test-selector="rapid-delivery-container"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          width="full"
          paddingBottom="18px"
        >
          <Text variant="heading1">Rapid Delivery</Text>
        </Flex>
        <Flex
          flexDirection="column"
          gap="4px"
          marginBottom="16px"
          width="full" borderTop="1px"
          borderColor="gray200"
          paddingTop="24px"
          >
          <Text variant="text6" data-test-selector="rapid-delivery-estimate">
            Get your shop delivered in under {deliveryEstimate} minutes.
          </Text>
          {rapidDeliveryShippingCost && (
            <Flex gap="4px">
              <Text variant="text3">
                Delivery fee: 
              </Text>
              <Text variant="text5" data-test-selector="rapid-delivery-shipping-cost">
                £{rapidDeliveryShippingCost.toFixed(2)}
              </Text>
            </Flex>
          )}
        </Flex>
        <Flex flexDirection="column" gap="24px" alignItems="flex-start" width="full">
          <BaseButton
            size="sm"
            variant="primary"
            maxHeight={["48px", "40px"]}
            width={["auto", "200px"]}
            onClick={!isRapidDeliveryBooked ? handleBookRapidDelivery : undefined}
            data-test-selector="rapid-delivery-book-button"
          >
            {isRapidDeliveryBooked && <CheckIcon marginRight="2px" />}
            <Text variant="unstyled" fontSize="inherit" lineHeight="inherit" paddingX="10px">
              {getButtonText()}
            </Text>
          </BaseButton>
        
          {/* Countdown */}
          {isRapidDeliveryBooked &&
            <Box 
              width={["full", "half"]}
              backgroundColor="gray50"
              paddingY="12px" paddingX="16px" style={{borderRadius: "4px"}} data-test-selector="rapid-delivery-countdown">
              <Flex gap="4px" alignItems="center" justifyContent="center" flexWrap="wrap">
                <Text variant="text3">Checkout in the next:</Text>
                {expiryTime ? (
                  <Countdown
                    slotExpiration={expiryTime}
                    color="accent0"
                    textVariant="text2"
                  />
                ) : (
                  <Text variant="text2">--:--</Text>
                )}
                <Text variant="text3">to secure your delivery</Text>
              </Flex>
            </Box>
          }
        </Flex>
      </Flex>

      <RapidDeliveryConfirmationModal
        {...confirmationModal}
      />
    </>
  )
})
